import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Assessment as ReportIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const ProductionGradeReport = () => {
  const [filters, setFilters] = useState({
    from_date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    to_date: new Date().toISOString().split('T')[0],
    steel_code: '',
    die_no: '',
    ht_code: ''
  });

  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  const { data: reportData, isLoading, error, refetch } = useQuery(
    ['productionGradeReport', filters],
    async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      // This would call a backend API that aggregates production grade data
      const response = await api.get(`/reports/production-grade?${params}`);
      return response.data;
    },
    {
      onError: (error) => {
        enqueueSnackbar(`Error fetching production grade report: ${error.message}`, { variant: 'error' });
      },
      enabled: false // Don't auto-fetch until API is implemented
    }
  );

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    enqueueSnackbar('Download functionality will be implemented', { variant: 'info' });
  };

  const handleGenerateReport = () => {
    refetch();
  };

  // Mock data for demonstration since API isn't implemented yet
  const mockData = [
    {
      steel_code: 'EN24',
      steel_name: 'EN24 Alloy Steel',
      total_production: 150.5,
      total_parts: 1250,
      grade_a_count: 1100,
      grade_b_count: 120,
      grade_c_count: 30,
      reject_count: 0,
      efficiency: 95.2,
      avg_hardness: 42.5
    },
    {
      steel_code: '4140',
      steel_name: '4140 Chromoly Steel',
      total_production: 89.3,
      total_parts: 750,
      grade_a_count: 680,
      grade_b_count: 60,
      grade_c_count: 10,
      reject_count: 0,
      efficiency: 97.3,
      avg_hardness: 38.2
    },
    {
      steel_code: 'C45',
      steel_name: 'C45 Medium Carbon Steel',
      total_production: 203.8,
      total_parts: 1850,
      grade_a_count: 1600,
      grade_b_count: 200,
      grade_c_count: 45,
      reject_count: 5,
      efficiency: 92.1,
      avg_hardness: 35.8
    }
  ];

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A': return 'success';
      case 'B': return 'warning';
      case 'C': return 'info';
      default: return 'error';
    }
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading production grade report: {error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ReportIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" gutterBottom color="primary">
          Production Grade Report
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Report Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Steel Code"
              value={filters.steel_code}
              onChange={(e) => setFilters({ ...filters, steel_code: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Die Number"
              value={filters.die_no}
              onChange={(e) => setFilters({ ...filters, die_no: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>HT Code</InputLabel>
              <Select
                value={filters.ht_code}
                label="HT Code"
                onChange={(e) => setFilters({ ...filters, ht_code: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="A">Annealing</MenuItem>
                <MenuItem value="N">Normalizing</MenuItem>
                <MenuItem value="H">Hardening</MenuItem>
                <MenuItem value="T">Tempering</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleGenerateReport}
              startIcon={<SearchIcon />}
            >
              Generate Report
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={3} display="flex" gap={2}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Export to Excel
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="primary">
                    {mockData.reduce((sum, item) => sum + item.total_production, 0).toFixed(1)} T
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Production
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="success.main">
                    {mockData.reduce((sum, item) => sum + item.grade_a_count, 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Grade A Parts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="warning.main">
                    {mockData.reduce((sum, item) => sum + item.grade_b_count, 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Grade B Parts
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" color="info.main">
                    {(mockData.reduce((sum, item) => sum + item.efficiency, 0) / mockData.length).toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Avg Efficiency
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Production Grade Table */}
          <Paper>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6">
                Production Grade Analysis
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Period: {filters.from_date} to {filters.to_date}
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Steel Code</strong></TableCell>
                    <TableCell><strong>Steel Name</strong></TableCell>
                    <TableCell align="right"><strong>Total Production (T)</strong></TableCell>
                    <TableCell align="right"><strong>Total Parts</strong></TableCell>
                    <TableCell align="center"><strong>Grade A</strong></TableCell>
                    <TableCell align="center"><strong>Grade B</strong></TableCell>
                    <TableCell align="center"><strong>Grade C</strong></TableCell>
                    <TableCell align="center"><strong>Rejects</strong></TableCell>
                    <TableCell align="right"><strong>Efficiency %</strong></TableCell>
                    <TableCell align="right"><strong>Avg Hardness</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No production grade data found for the selected criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    mockData.map((record, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Chip 
                            label={record.steel_code} 
                            color="primary" 
                            variant="outlined" 
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{record.steel_name}</TableCell>
                        <TableCell align="right">{record.total_production}</TableCell>
                        <TableCell align="right">{record.total_parts}</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={record.grade_a_count} 
                            color="success" 
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={record.grade_b_count} 
                            color="warning" 
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={record.grade_c_count} 
                            color="info" 
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={record.reject_count} 
                            color={record.reject_count > 0 ? "error" : "default"} 
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            color={record.efficiency >= 95 ? "success.main" : record.efficiency >= 90 ? "warning.main" : "error.main"}
                            fontWeight="bold"
                          >
                            {record.efficiency}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{record.avg_hardness} HRC</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ProductionGradeReport;